import { StyleSheet } from 'react-native';
import { WARMUP_COLORS } from '../../../src/quiz/constants/warmup.constants';

/**
 * Styles for the first-run how-to-play sheet. Values mirror the handoff design
 * (`Warmup Soccer Wordle.dc.html` → "HOW-TO-PLAY INTRO"). Demo-only swatch/tile colours that are unique
 * to this sheet live here; shared tokens come from WARMUP_COLORS.
 */

const TILE_GREEN = WARMUP_COLORS.green; // #6EE7B7
const TILE_AMBER = WARMUP_COLORS.amber; // #FCD34D
const TILE_GREY = WARMUP_COLORS.absent; // #374151
const ON_BRIGHT = WARMUP_COLORS.textOnBright; // #0F1720

export const warmupIntroSheetStyles = StyleSheet.create({
  overlay: {
    ...StyleSheet.absoluteFillObject,
    justifyContent: 'flex-end',
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(8,5,15,0.74)',
  },
  skipHint: {
    position: 'absolute',
    top: 54,
    left: 0,
    right: 0,
    textAlign: 'center',
    fontSize: 12,
    fontWeight: '600',
    color: WARMUP_COLORS.textFaint,
  },
  sheet: {
    backgroundColor: WARMUP_COLORS.card,
    borderTopLeftRadius: 26,
    borderTopRightRadius: 26,
    borderTopWidth: 1,
    borderColor: '#2A2238',
    paddingTop: 14,
    paddingHorizontal: 22,
    paddingBottom: 26,
  },
  handle: {
    width: 40,
    height: 4,
    borderRadius: 9999,
    backgroundColor: TILE_GREY,
    alignSelf: 'center',
    marginBottom: 18,
  },

  // ---- header ----
  header: {
    alignItems: 'center',
  },
  eyebrow: {
    fontSize: 11,
    fontWeight: '800',
    letterSpacing: 1.3,
    textTransform: 'uppercase',
  },
  title: {
    fontSize: 22,
    fontWeight: '800',
    color: WARMUP_COLORS.text,
    marginTop: 3,
    textAlign: 'center',
  },

  // ---- footer (shared) ----
  footer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 7,
    marginTop: 16,
  },
  footerGlyph: {
    fontSize: 13,
  },
  footerText: {
    fontSize: 12.5,
    fontWeight: '600',
    color: WARMUP_COLORS.textFaint,
  },

  // ---- play button (shared) ----
  playButton: {
    height: 54,
    borderRadius: 13,
    backgroundColor: WARMUP_COLORS.purple,
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: 20,
  },
  playButtonText: {
    fontSize: 16,
    fontWeight: '800',
    color: '#111827',
  },
  pressed: {
    opacity: 0.85,
  },

  // ---- Wordle demo tiles ----
  tilesRow: {
    flexDirection: 'row',
    gap: 8,
    justifyContent: 'center',
    marginTop: 22,
  },
  tile: {
    width: 50,
    height: 50,
    borderRadius: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  tileGreen: { backgroundColor: TILE_GREEN },
  tileAmber: { backgroundColor: TILE_AMBER },
  tileGrey: { backgroundColor: TILE_GREY },
  tileLetter: {
    fontSize: 24,
    fontWeight: '800',
  },
  tileLetterOnBright: { color: ON_BRIGHT },
  tileLetterDim: { color: WARMUP_COLORS.textDim },

  // ---- legend (Wordle + Footballer) ----
  legend: {
    marginTop: 24,
    gap: 11,
  },
  legendRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  legendSwatch: {
    width: 26,
    height: 26,
    borderRadius: 7,
  },
  legendText: {
    fontSize: 14.5,
    fontWeight: '600',
    color: '#E5E7EB',
  },
  legendTextDim: {
    color: WARMUP_COLORS.textDim,
  },

  // ---- Higher or Lower demo ----
  hiloRows: {
    marginTop: 20,
    gap: 8,
  },
  hiloRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    borderRadius: 14,
    paddingVertical: 11,
    paddingHorizontal: 14,
  },
  hiloRowKnown: {
    backgroundColor: WARMUP_COLORS.surfaceAlt,
  },
  hiloRowMystery: {
    backgroundColor: WARMUP_COLORS.card,
    borderWidth: 1,
    borderColor: '#2A2238',
  },
  hiloAvatar: {
    width: 38,
    height: 38,
    borderRadius: 19,
    alignItems: 'center',
    justifyContent: 'center',
  },
  hiloAvatarKnown: { backgroundColor: '#2A4C82' },
  hiloAvatarMystery: { backgroundColor: '#5E3D9C' },
  hiloAvatarText: {
    fontSize: 14,
    fontWeight: '800',
    color: '#FFFFFF',
  },
  hiloName: {
    flex: 1,
    fontSize: 15,
    fontWeight: '700',
    color: WARMUP_COLORS.text,
  },
  hiloValue: {
    fontSize: 20,
    fontWeight: '800',
    color: '#34D399',
    textAlign: 'right',
  },
  hiloValueLabel: {
    fontSize: 9,
    fontWeight: '700',
    color: WARMUP_COLORS.textFaint,
    letterSpacing: 0.6,
    textAlign: 'right',
    marginTop: 1,
  },
  hiloMysteryMark: {
    fontSize: 24,
    fontWeight: '800',
    color: WARMUP_COLORS.purple,
  },
  hiloChips: {
    flexDirection: 'row',
    gap: 10,
    marginTop: 14,
  },
  hiloChip: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 7,
    minHeight: 46,
    borderRadius: 12,
  },
  hiloChipMore: { backgroundColor: '#34D399' },
  hiloChipFewer: { backgroundColor: WARMUP_COLORS.danger },
  hiloChipArrow: {
    fontSize: 16,
    fontWeight: '800',
  },
  hiloChipArrowMore: { color: ON_BRIGHT },
  hiloChipArrowFewer: { color: '#3A0E0E' },
  hiloChipLabel: {
    fontSize: 14,
    fontWeight: '800',
  },
  hiloChipLabelMore: { color: ON_BRIGHT },
  hiloChipLabelFewer: { color: '#3A0E0E' },

  // ---- Footballer demo (labelled attribute tiles) ----
  fqLabels: {
    flexDirection: 'row',
    gap: 6,
    marginTop: 22,
  },
  fqLabelCell: {
    flex: 1,
    alignItems: 'center',
  },
  fqLabelText: {
    fontSize: 10,
    fontWeight: '800',
    letterSpacing: 0.6,
    color: WARMUP_COLORS.textFaint,
  },
  fqTilesRow: {
    flexDirection: 'row',
    gap: 6,
    marginTop: 6,
  },
  fqTile: {
    flex: 1,
    height: 46,
    borderRadius: 9,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 2,
  },
  fqTileEmpty: {
    backgroundColor: '#1A2030',
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.08)',
  },
  fqTileText: {
    fontSize: 12,
    fontWeight: '800',
  },
  fqTileTextOnBright: { color: ON_BRIGHT },
  fqTileTextAmber: { color: '#3A2C05' },
  fqTileTextDim: { color: WARMUP_COLORS.textDim },
  fqTileFlag: {
    fontSize: 18,
  },
  fqTileArrow: {
    fontSize: 11,
    fontWeight: '800',
    color: WARMUP_COLORS.danger, // ▼ on a grey age tile — visible (matches the in-game red/green arrows)
    marginTop: -1,
  },
  fqArrowNote: {
    marginTop: 14,
    fontSize: 12.5,
    fontWeight: '600',
    color: WARMUP_COLORS.textFaint,
    textAlign: 'center',
  },

  // ---- Keeper Duel ----
  kdLegendGlyph: {
    fontSize: 24,
    width: 32,
    textAlign: 'center',
  },
  kdLegendText: {
    flex: 1,
  },
});
